import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { addEntities, addEntity, removeEntity, setAllEntities, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { pipe, switchMap } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { Response } from '../../../../shared/models/response.model';
import { withSelectedEntity } from '../../../../shared/store/selected-entity.feature';
import { IndiceRésultats } from '../models/indice-résultats';
import { IndiceRésultatsService } from '../processus-indice-résultats.service';
import { IndiceCreateRequest } from '../requests/create-request';
import { IndiceUpdateRequest } from '../requests/update-request';

export const IndiceRésultatsStore = signalStore(
  { providedIn: 'root' },
  withEntities<IndiceRésultats>(),
  withSelectedEntity(),
  withMethods((store) => ({
    chargerIndices: (state: { indices: IndiceRésultats[] }) => patchState(store, setAllEntities(state.indices)),
    ajouterIndice: (indice: IndiceRésultats) => patchState(store, addEntity(indice)),
    ajouterIndices: (indices: IndiceRésultats[]) => patchState(store, addEntities(indices)),
    supprimerIndice: (id: string) => patchState(store, removeEntity(id)),
    selectionerIndice: (state: { idIndice: string }) => patchState(store, { selectedEntityId: state.idIndice }),
    deselectionerIndice: () => patchState(store, { selectedEntityId: null }),
  })),
  withMethods((store, indiceRésultatsService = inject(IndiceRésultatsService)) => ({
    charger: rxMethod<string>(
      pipe(
        switchMap((référentielId) =>
          indiceRésultatsService.trouverTous(référentielId).pipe(
            tapResponse({
              next: (result: Response<IndiceRésultats>) => store.chargerIndices({ indices: result.items }),
              error: (err) => {
                console.error(err);
                const toastrId = uuidv7();
                toast.custom(ToastrErrorComponent, {
                  componentProps: {
                    message: `Un problème est survenu lors de la récupération des indices de résultats, contactez le support.`,
                    id: toastrId,
                  },
                  id: toastrId,
                });
              },
            }),
          ),
        ),
      ),
    ),
    créer: rxMethod<{ data: IndiceCreateRequest; next: () => void; error: () => void }>(
      pipe(
        switchMap(({ data, next, error }) =>
          indiceRésultatsService.créer(data).pipe(
            tapResponse({
              next: (result) => {
                store.ajouterIndices(result);
                next();
              },
              error: (err: HttpErrorResponse) => {
                console.error(err);
                const toastrId = uuidv7();
                toast.custom(ToastrErrorComponent, {
                  componentProps: {
                    message:
                      err.error.detail ??
                      `Un problème est survenu lors de la création d'un indice de résultats, contactez le support.`,
                    id: toastrId,
                  },
                  id: toastrId,
                });
                error();
              },
            }),
          ),
        ),
      ),
    ),
    modifier: rxMethod<{
      data: IndiceUpdateRequest;
      next: () => void;
      error?: () => void;
    }>(
      pipe(
        switchMap(({ data, next, error }) =>
          indiceRésultatsService.modifier(data).pipe(
            tapResponse({
              next: () => next(),
              error: (err: HttpErrorResponse) => {
                console.error(err);
                const toastrId = uuidv7();
                toast.custom(ToastrErrorComponent, {
                  componentProps: {
                    message:
                      err.error.detail ??
                      `Un problème est survenu lors de la modification d'un indice de résultats, contactez le support.`,
                    id: toastrId,
                  },
                  id: toastrId,
                });
                error?.();
              },
            }),
          ),
        ),
      ),
    ),
    supprimer: rxMethod<{ idIndice: string; next: () => void }>(
      pipe(
        switchMap(({ idIndice, next }) =>
          indiceRésultatsService.supprimer(idIndice).pipe(
            tapResponse({
              next: () => {
                store.supprimerIndice(idIndice);
                next();
              },
              error: (err) => {
                console.error(err);
                const toastrId = uuidv7();
                toast.custom(ToastrErrorComponent, {
                  componentProps: {
                    message: `Un problème est survenu lors de la suppression de l'indice de résulats, contactez le support.`,
                    id: toastrId,
                  },
                  id: toastrId,
                });
              },
            }),
          ),
        ),
      ),
    ),
    selection: rxMethod<{
      idIndice: string;
    }>(
      pipe(
        switchMap((params) => indiceRésultatsService.trouverParId(params.idIndice)),
        tapResponse({
          next: (indice) => store.ajouterIndice(indice),
          error: (err) => {
            console.error(err);
            const toastrId = uuidv7();
            toast.custom(ToastrErrorComponent, {
              componentProps: {
                message: `Un problème est survenu lors de la récupération de l'indice de résultats, contactez le support.`,
                id: toastrId,
              },
              id: toastrId,
            });
          },
        }),
      ),
    ),
  })),
);
